import { Show, createMemo } from "solid-js";
import type { BodyWeightLog } from "../api/types";
import { t } from "../i18n";
import { layoutSeries } from "../lib/chart";

interface Props {
  /** Newest first or any order — will sort ascending by date. */
  logs: BodyWeightLog[];
  height?: number;
}

export default function WeightChart(props: Props) {
  const points = createMemo(() => {
    const sorted = [...props.logs].sort((a, b) =>
      a.date.localeCompare(b.date)
    );
    return sorted.map((l) => ({ date: l.date, weight: l.weight }));
  });

  const layout = createMemo(() => {
    const pts = points();
    const L = layoutSeries(pts, (p) => p.weight, { h: props.height });
    if (!L) return null;
    return {
      ...L,
      minLabel: Math.round(L.min * 10) / 10,
      maxLabel: Math.round(L.max * 10) / 10,
      firstDate: L.coords[0].date,
      lastDate: L.coords[L.coords.length - 1].date,
    };
  });

  return (
    <Show
      when={layout()}
      fallback={
        <div class="flex h-40 items-center justify-center bg-[var(--surface)] text-[15px] text-ink-muted">
          {t("NO_WEIGHT_DATA")}
        </div>
      }
    >
      {(L) => (
        <div class="space-y-2">
          <svg
            viewBox={`0 0 ${L().w} ${L().h}`}
            class="h-auto w-full overflow-visible"
            role="img"
            aria-label={t("WEIGHT_CHART")}
          >
            <defs>
              <linearGradient id="weightFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stop-color="var(--accent)" stop-opacity="0.28" />
                <stop offset="100%" stop-color="var(--accent)" stop-opacity="0" />
              </linearGradient>
            </defs>
            <line
              x1="12"
              y1={L().h / 2}
              x2={L().w - 12}
              y2={L().h / 2}
              stroke="var(--separator)"
              stroke-width="1"
            />
            <path d={L().area} fill="url(#weightFill)" />
            <path
              d={L().line}
              fill="none"
              stroke="var(--accent)"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            {L().coords.map((c) => (
              <circle
                cx={c.x}
                cy={c.y}
                r="3.5"
                fill="var(--surface)"
                stroke="var(--accent)"
                stroke-width="2"
              />
            ))}
          </svg>
          <div class="flex justify-between text-[13px] text-ink-muted">
            <span>{L().firstDate}</span>
            <span>
              {L().minLabel}–{L().maxLabel} {t("KG")}
            </span>
            <span>{L().lastDate}</span>
          </div>
        </div>
      )}
    </Show>
  );
}
